// src/components/ScrollActions.js

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';

// Material-UI Core Components
import Box from '@mui/material/Box';
import SpeedDial from '@mui/material/SpeedDial';
import SpeedDialAction from '@mui/material/SpeedDialAction';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Fade from '@mui/material/Fade';
import Backdrop from '@mui/material/Backdrop';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';

// Tabler Icons
import { 
  IconHeadset, 
  IconLanguage, 
  IconCurrencyDollar, 
  IconArrowUp, 
  IconCompass, 
  IconX,
  IconShare,
  IconBrandTripadvisor,
  IconBrandGoogle,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandYoutube,
  IconBrandTiktok
} from '@tabler/icons-react';

// Custom Contexts
import { useLanguage } from '../context/LanguageContext';
import { useCurrency } from '../context/CurrencyContext';

// --- Data for Menus and Actions (remains the same) ---
const languages = [ { code: 'en', name: 'English', country_code: 'US' }, { code: 'de', name: 'Deutsch (German)', country_code: 'DE' }, { code: 'fr', name: 'Français (French)', country_code: 'FR' }, { code: 'es', name: 'Español (Spanish)', country_code: 'ES' }, { code: 'it', name: 'Italiano (Italian)', country_code: 'IT' }, { code: 'zh', name: '中文 (Chinese)', country_code: 'CN' }, { code: 'ja', name: '日本語 (Japanese)', country_code: 'JP' }, { code: 'ru', name: 'Русский (Russian)', country_code: 'RU' }, { code: 'ar', name: 'العربية (Arabic)', country_code: 'SA' }, { code: 'hi', name: 'हिन्दी (Hindi)', country_code: 'IN' }, ];
const currencies = [ { code: 'USD', name: 'USD - US Dollar', country_code: 'US' }, { code: 'EUR', name: 'EUR - Euro', country_code: 'EU' }, { code: 'GBP', name: 'GBP - British Pound', country_code: 'GB' }, { code: 'LKR', name: 'LKR - Sri Lankan Rupee', country_code: 'LK' }, { code: 'INR', name: 'INR - Indian Rupee', country_code: 'IN' }, { code: 'AUD', name: 'AUD - Australian Dollar', country_code: 'AU' }, { code: 'CAD', name: 'CAD - Canadian Dollar', country_code: 'CA' }, { code: 'CNY', name: 'CNY - Chinese Yuan', country_code: 'CN' }, { code: 'JPY', name: 'JPY - Japanese Yen', country_code: 'JP' }, { code: 'RUB', name: 'RUB - Russian Ruble', country_code: 'RU' }, { code: 'CHF', name: 'CHF - Swiss Franc', country_code: 'CH' }, { code: 'AED', name: 'AED - UAE Dirham', country_code: 'AE' }, ];
const socialActions = [ { icon: <IconBrandYoutube />, name: 'YouTube', link: '#' }, { icon: <IconBrandTiktok />, name: 'TikTok', link: '#' }, { icon: <IconBrandInstagram />, name: 'Instagram', link: '#' }, { icon: <IconBrandFacebook />, name: 'Facebook', link: '#' }, { icon: <IconBrandGoogle />, name: 'Google Reviews', link: '#' }, { icon: <IconBrandTripadvisor />, name: 'TripAdvisor', link: '#' }, ];


// --- ANIMATION DEFINITIONS ---
const pulse = keyframes`
  0% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.7); }
  70% { transform: scale(1); box-shadow: 0 0 0 10px rgba(255, 255, 255, 0); }
  100% { transform: scale(0.95); box-shadow: 0 0 0 0 rgba(255, 255, 255, 0); }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const RotatingSpeedDial = styled(SpeedDial)(({ theme }) => ({
  '& .MuiFab-primary': {
    animation: `${spin} 5s linear infinite`,
  },
}));

const PulsingSpeedDial = styled(SpeedDial)(({ theme }) => ({
  '& .MuiFab-primary:hover': {
    animation: `${pulse} 2s infinite`,
  },
}));


const ScrollActions = () => {
  const { t } = useTranslation();
  const { setLanguage } = useLanguage();
  const { setCurrency } = useCurrency();

  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 100 });
  const [menuAnchorEl, setMenuAnchorEl] = useState({ language: null, currency: null });
  const [socialDialOpen, setSocialDialOpen] = useState(false);
  const handleSocialOpen = () => setSocialDialOpen(true);
  const handleSocialClose = () => setSocialDialOpen(false);
  const handleMenuOpen = (menu) => (event) => setMenuAnchorEl({ ...menuAnchorEl, [menu]: event.currentTarget });
  const handleMenuClose = (menu) => () => setMenuAnchorEl({ ...menuAnchorEl, [menu]: null });
  const handleLanguageChange = (lang) => { setLanguage(lang); handleMenuClose('language')(); };
  const handleCurrencyChange = (curr) => { setCurrency(curr); handleMenuClose('currency')(); };
  const handleScrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });
  const handleSocialClick = (url) => { window.open(url, '_blank', 'noopener,noreferrer'); handleSocialClose(); };

  const quickActions = [
    { icon: <IconArrowUp />, name: t('scroll_to_top'), onClick: handleScrollToTop },
    { icon: <IconHeadset />, name: t('talk_to_expert'), onClick: () => { window.location.href = 'tel:+94716059651'; } },
    { icon: <IconCurrencyDollar />, name: t('change_currency'), onClick: handleMenuOpen('currency') },
    { icon: <IconLanguage />, name: t('change_language'), onClick: handleMenuOpen('language') },
  ];
  const accentColor = '#D4A259';

  return (
    <>
      <Fade in={trigger}>
        <Box
          sx={{
            position: 'fixed',
            bottom: 32,
            right: 32,
            zIndex: 1300,
            display: 'flex',
            flexDirection: 'column-reverse',
            alignItems: 'flex-end',
            gap: 2,
            // --- FIX 1: Allow clicks to pass through this container ---
            pointerEvents: 'none', 
          }}
        >
          {/* DIAL 1: Social Media */}
          <Backdrop open={socialDialOpen} sx={{ zIndex: -1 }} />
          <PulsingSpeedDial
            ariaLabel="Social Media Links"
            icon={<IconShare size={24} />}
            onClose={handleSocialClose}
            onOpen={handleSocialOpen}
            open={socialDialOpen}
            direction="left"
            sx={{
              // --- FIX 2: Re-enable clicks specifically for this button ---
              pointerEvents: 'auto', 
              '& .MuiFab-primary': {
                backgroundColor: '#333',
                color: 'white',
                '&:hover': { backgroundColor: '#555' },
              },
            }}
          >
            {socialActions.map((action) => (
              <SpeedDialAction
                key={action.name}
                icon={action.icon}
                tooltipTitle={action.name}
                tooltipPlacement="top"
                onClick={() => handleSocialClick(action.link)}
              />
            ))}
          </PulsingSpeedDial>

          {/* DIAL 2: Quick Actions */}
          <RotatingSpeedDial
            ariaLabel="Quick Actions"
            icon={<IconCompass size={24} />}
            openIcon={<IconX size={24} />}
            direction="up"
            sx={{
              // --- FIX 3: Re-enable clicks specifically for this button ---
              pointerEvents: 'auto',
              '& .MuiFab-primary': {
                backgroundColor: accentColor,
                '&:hover': { backgroundColor: '#c5934e' },
              },
            }}
          >
            {quickActions.map((action) => (
              <SpeedDialAction
                key={action.name}
                icon={action.icon}
                tooltipTitle={action.name}
                onClick={action.onClick}
              />
            ))}
          </RotatingSpeedDial>
        </Box>
      </Fade>

      {/* Menus remain the same */}
      <Menu anchorEl={menuAnchorEl.language} open={Boolean(menuAnchorEl.language)} onClose={handleMenuClose('language')} > {languages.map((lang) => ( <MenuItem key={lang.code} onClick={() => handleLanguageChange(lang.code)}> <ReactCountryFlag countryCode={lang.country_code} svg style={{ width: '1.5em', height: '1.5em', marginRight: '10px' }} title={lang.name} /> {lang.name} </MenuItem> ))} </Menu>
      <Menu anchorEl={menuAnchorEl.currency} open={Boolean(menuAnchorEl.currency)} onClose={handleMenuClose('currency')} > {currencies.map((curr) => ( <MenuItem key={curr.code} onClick={() => handleCurrencyChange(curr.code)}> <ReactCountryFlag countryCode={curr.country_code} svg style={{ width: '1.5em', height: '1.5em', marginRight: '10px' }} title={curr.name} /> {curr.name} </MenuItem> ))} </Menu>
    </>
  );
};

export default ScrollActions;